import { useEffect, useState } from "react";
import qs from "qs";
import { useHistory, useLocation } from "react-router";
import moment from "antd/node_modules/moment";

export type BaseFilters = {
  page: number;
  size: number;
  keyword: string;
  period: (moment.Moment | null)[];
};

export const baseFilters = () => {
  return {
    page: 0,
    size: 20,
    keyword: "",
    period: [null, null],
  };
};

export const useFilters = <F extends BaseFilters>(initData: F, parse?: (params: any) => Partial<F>, sort = true) => {
  const history = useHistory();
  const location = useLocation();
  const [filters, setFilters] = useState({ ...initData });
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchKey, setSearchKey] = useState(0);
  const [sortedInfo, setSortedInfo] = useState({ columnKey: "CreatedAt", order: "descend" });

  useEffect(() => {
    const params = qs.parse?.(location.search, { ignoreQueryPrefix: true }) ?? {};
    let period: (moment.Moment | null)[] = [null, null];
    if (params.period) {
      period = params.period?.map((x: string) => (x ? moment(x) : null));
    }
    setFilters({
      ...initData,
      page: Number(params.page) || 0,
      size: Number(params.size) || 20,
      period,
      keyword: params.keyword ?? "",
      ...parse?.(params),
    });
    refresh();
  }, [location.search]);

  useEffect(() => {
    const period = filters.period?.map((x) => (x ? x.format("YYYY-MM-DD") : ""));
    const queryString = qs.stringify({ ...filters, period }, { addQueryPrefix: true });
    history.push({ search: queryString });
  }, [searchKey, history]);

  useEffect(() => {
    if (sort) {
      refresh();
    }
  }, [sortedInfo]);

  const search = (newFilters: Partial<F>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setSearchKey((prev) => prev + 1);
  };

  const refresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const reset = () => {
    setFilters({ ...initData });
    setSearchKey((prev) => prev + 1);
  };

  const getFiltersData = () => {
    let period: any[] = [];
    if (filters.period?.[0]) {
      period = [filters.period[0]?.startOf("day").toISOString(), filters.period?.[1]?.endOf("day").toISOString()];
    }
    const sorts = sort
      ? sortedInfo.order
        ? [{ field: sortedInfo.columnKey, order: sortedInfo.order === "descend" ? "desc" : "asc" }]
        : []
      : undefined;

    return {
      ...filters,
      period,
      sorts,
    };
  };

  return {
    filters,
    setFilters,
    refreshKey,
    searchKey,
    search,
    refresh,
    reset,
    getFiltersData,
    sortedInfo,
    setSortedInfo,
  };
};
